import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/api/client";
import AuthShell, { AuthField, authInputClass } from "@/components/AuthShell";
import { useAuth } from "@/lib/auth";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [form, setForm] = useState({ full_name: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError("This link is missing its invite code.");
      return;
    }
    api.auth
      .readInvite(token)
      .then((data) => {
        setInvite(data);
        setForm((f) => ({ ...f, full_name: data.full_name || "" }));
      })
      .catch((err) => setLoadError(err.message));
  }, [token]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const mismatch = form.confirm.length > 0 && form.password !== form.confirm;
  const ready = form.full_name && form.password.length >= 8 && !mismatch;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { user } = await api.auth.acceptInvite(token, {
        full_name: form.full_name,
        password: form.password,
      });
      setUser(user);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <AuthShell title="Invite not valid" subtitle={loadError}>
        <p className="text-white/60 text-sm">
          Ask an admin to send you a fresh invite link, then open it again.
        </p>
      </AuthShell>
    );
  }

  if (!invite) {
    return (
      <AuthShell title="App Hub">
        <p className="text-white/50 text-sm text-center">Checking your invite…</p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Welcome to App Hub" subtitle={`Setting up the account for ${invite.email}`}>
      <form onSubmit={submit} className="space-y-4">
        <AuthField label="Your name">
          <input value={form.full_name} onChange={set("full_name")} className={authInputClass} autoFocus required />
        </AuthField>

        <AuthField label="Choose a password" hint="At least 8 characters.">
          <input
            type="password"
            value={form.password}
            onChange={set("password")}
            className={authInputClass}
            autoComplete="new-password"
            required
          />
        </AuthField>

        <AuthField label="Confirm password">
          <input
            type="password"
            value={form.confirm}
            onChange={set("confirm")}
            className={authInputClass}
            autoComplete="new-password"
            required
          />
        </AuthField>

        {mismatch && <p className="text-amber-300 text-sm">Those passwords don't match.</p>}
        {error && <p className="text-red-300 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>}

        <button
          type="submit"
          disabled={busy || !ready}
          className="w-full bg-purple-600 hover:bg-purple-500 disabled:opacity-40 disabled:hover:bg-purple-600 text-white rounded-xl py-2.5 text-sm font-medium transition-colors"
        >
          {busy ? "Setting up…" : "Create my account"}
        </button>
      </form>
    </AuthShell>
  );
}
